import React, { useId } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

const BRAND_FALLBACK = '#006766';

/**
 * Label-above text field for listing modals — matches Property status / date rows.
 * Avoids MUI outlined floating labels and the notch overlap bug.
 */
const ListingModalTextField = ({
  label,
  id: idProp,
  disabled = false,
  helperText,
  fullWidth = true,
  sx,
  ...textFieldProps
}) => {
  const theme = useTheme();
  const autoId = useId();
  const inputId = idProp || autoId;
  const brand =
    theme?.palette?.customPrimary?.main ||
    theme?.palette?.primary?.main ||
    BRAND_FALLBACK;

  return (
    <Box sx={{ width: fullWidth ? '100%' : undefined }}>
      <Typography
        component="label"
        htmlFor={inputId}
        sx={{
          display: 'block',
          fontSize: '14px',
          fontWeight: 500,
          color: disabled ? 'rgba(0,0,0,0.38)' : 'rgba(0,0,0,0.55)',
          mb: 0.75,
        }}
      >
        {label}
      </Typography>
      <TextField
        {...textFieldProps}
        id={inputId}
        hiddenLabel
        disabled={disabled}
        fullWidth={fullWidth}
        variant="outlined"
        helperText={helperText}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '14px',
            backgroundColor: 'rgba(0,0,0,0.04) !important',
            fontSize: '16px',
            minHeight: 48,
            transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
            '& fieldset': {
              borderColor: 'rgba(0,0,0,0.1)',
              transition: 'border-color 0.15s ease',
            },
            '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.14)' },
            '&.Mui-focused': {
              backgroundColor: '#ffffff !important',
              boxShadow: `0 0 0 3px ${alpha(brand, 0.14)}`,
            },
            '&.Mui-focused fieldset': {
              borderColor: alpha(brand, 0.5),
              borderWidth: '1px',
            },
            '&.Mui-error fieldset': {
              borderColor: alpha(theme.palette.error.main, 0.55),
            },
            '&.Mui-error': {
              backgroundColor: `${alpha(theme.palette.error.main, 0.03)} !important`,
              boxShadow: `0 0 0 3px ${alpha(theme.palette.error.main, 0.1)}`,
            },
          },
          '& .MuiOutlinedInput-input': {
            py: '12px',
          },
          '& .MuiFormHelperText-root': {
            mx: 0.25,
            mt: 0.75,
            fontSize: '0.72rem',
          },
          ...sx,
        }}
      />
    </Box>
  );
};

export default ListingModalTextField;
